import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverMenu, Text, useFormField, usePopoverMenu } from '@frontend/design-system';
import { FilterableRadioList } from '../../../components/filterable-radio-list';

type SingleSelectTriggerProps = {
  label: string;
  selectedLabel?: string;
  isSelected: boolean;
  leadingIcon: IconName;
  getTriggerProps: ReturnType<typeof usePopoverMenu>['getTriggerProps'];
  isOpen: boolean;
  fullWidth?: boolean;
};

const SingleSelectTrigger = ({
  label,
  selectedLabel,
  isSelected,
  leadingIcon,
  getTriggerProps,
  isOpen,
  fullWidth,
}: SingleSelectTriggerProps) => {
  return (
    <NakedButton css={[styles.trigger, fullWidth && styles.triggerFullWidth]} {...getTriggerProps()}>
      <Icon name={leadingIcon} size={16} color='light' />
      <Text size='medium' weight='bold' color='default'>
        {label}
      </Text>
      <div css={styles.rightGroup}>
        {isSelected && selectedLabel && (
          <Text as='span' size='small' weight='light' color='subdued'>
            {selectedLabel}
          </Text>
        )}
        <Icon
          name='caret-down-tiny'
          size={8}
          color='light'
          css={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </div>
    </NakedButton>
  );
};

type RadioListPopoverProps = {
  label: string;
  fieldName: string;
  leadingIcon: IconName;
  options: { value: string; label: string }[];
  radioField: ReturnType<typeof useFormField<'text'>>;
  fullWidth?: boolean;
  showValueAsLabel?: boolean;
  hideClear?: boolean;
};

export const RadioListPopover = ({
  label,
  fieldName,
  leadingIcon,
  options,
  radioField,
  fullWidth,
  showValueAsLabel = false,
  hideClear = false,
}: RadioListPopoverProps) => {
  const { getTriggerProps, getMenuProps } = usePopoverMenu({ placement: 'bottom-start' });
  const isOpen = getMenuProps().isOpen;
  const selectedValue = radioField.value;
  const isSelected = !!selectedValue;
  const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label;

  return (
    <>
      <SingleSelectTrigger
        label={showValueAsLabel ? selectedLabel || label : label}
        selectedLabel={showValueAsLabel ? undefined : selectedLabel}
        isSelected={isSelected}
        leadingIcon={leadingIcon}
        getTriggerProps={getTriggerProps}
        isOpen={isOpen}
        fullWidth={fullWidth}
      />
      <PopoverMenu {...getMenuProps()} css={styles.popover}>
        <FilterableRadioList fieldName={fieldName} list={options} radioField={radioField} hideClear={hideClear} />
      </PopoverMenu>
    </>
  );
};

const styles = {
  trigger: css`
    display: flex;
    align-items: center;
    white-space: nowrap;
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.5, 1.5)};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
  `,
  triggerFullWidth: css`
    width: 100%;
  `,
  rightGroup: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    margin-left: auto;
  `,
  popover: css`
    padding: ${theme.spacing(1)};
    min-width: 220px;
    max-height: 360px;
    overflow-y: auto;
  `,
};
